import { useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  useController,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import {
  ExtensionEvent,
  extensionFunctionType,
  extensionWebhookType,
  HookType,
} from '@rossum/api-client/hooks';
import {
  IconEye,
  IconEyeOff,
  IconInfoSquareRounded,
} from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon, Tooltip, Typography } from '@rossum/ui/material';
import TextFieldControl from '../../../../../components/ReactHookForm/controls/TextFieldControl';
import ValidationInput from '../../../../../components/ReactHookForm/ValidationInput';
import {
  docsLinks,
  EXTERNAL_CRONTAB_VALIDATOR,
} from '../../../../../constants/values';
import { italicText, linebreak, link } from '../../../../../lib/formaterValues';
import { SideloadValues } from '../../../../../types/extensions';
import { QueueMultiSelect } from '../../../../queues/select-queue/QueueMultiSelect';
import { EventMultiSelect } from '../../../components/EventMultiSelect';
import {
  defaultNodejsRuntime,
  defaultPythonRuntime,
  DefaultRuntime,
  runtimesConfig,
} from '../../extension-detail/config';
import { ExtensionTypeSelect } from './ExtensionTypeSelect';
import { RuntimeButton } from './RuntimeButton';

export type NewExtensionData = {
  description: string;
  events: Array<ExtensionEvent>;
  name: string;
  queues: Array<Url>;
  runtime: DefaultRuntime;
  secret: string;
  settings: Record<string, unknown>;
  secrets: Record<string, unknown>;
  sideload: Array<SideloadValues>;
  tokenOwner: Url | null;
  type: HookType;
  schedule: string;
  url: string;
  payloadLoggingEnabled: boolean;
};

type Props = {
  control: Control<NewExtensionData, unknown>;
  errors: FieldErrors<NewExtensionData>;
};

export const GeneralCreateSettings = ({ control, errors }: Props) => {
  const intl = useIntl();

  const [showSecret, setShowSecret] = useState(false);

  const {
    field: { value: watchType, onChange: onTypeChange },
  } = useController({ name: 'type', control });

  const {
    field: { value: watchRuntime, onChange: onWatchRuntimeChange },
  } = useController({ name: 'runtime', control });

  const {
    field: { value: watchEvents },
  } = useController({ name: 'events', control });

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Typography variant="body2">
          <FormattedMessage id="containers.settings.extensions.createExtension.name.label" />
        </Typography>
        <ValidationInput<NewExtensionData>
          control={control}
          name="name"
          getErrorMessage={(_, { message }) => message ?? ''}
          dataCy="extensions-name-input"
          placeholder={intl.formatMessage({
            id: `containers.settings.extensions.createExtension.name.placeholder`,
          })}
        />
      </Stack>
      <Stack spacing={1}>
        <Stack
          alignItems="flex-start"
          direction="row"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="body2">
            <FormattedMessage id="containers.settings.extensions.createExtension.description.label" />
          </Typography>
          <Typography variant="body2" color="secondary">
            <FormattedMessage id="containers.settings.extensions.createExtension.description.fieldDescription" />
          </Typography>
        </Stack>
        <TextFieldControl
          size="small"
          minRows={4}
          multiline
          ControllerProps={{ control, name: 'description' }}
          placeholder={intl.formatMessage({
            id: 'containers.settings.extensions.createExtension.description.placeholder',
          })}
          data-cy="extensions-description-input"
        />
      </Stack>
      <Stack spacing={1}>
        <Stack alignItems="flex-start" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2">
              <FormattedMessage id="containers.settings.extensions.createExtension.events.label" />
            </Typography>
            <Tooltip
              title={
                <FormattedMessage
                  id="containers.settings.extensions.function.events.tooltip"
                  values={{
                    link: link(docsLinks.webhookEvents),
                    linebreak,
                  }}
                />
              }
              placement="top"
            >
              <SvgIcon fontSize="medium" color="secondary">
                <IconInfoSquareRounded />
              </SvgIcon>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="secondary">
            <FormattedMessage id="containers.settings.extensions.createExtension.events.subLabel" />
          </Typography>
        </Stack>
        <Controller
          control={control}
          name="events"
          render={({ field }) => <EventMultiSelect {...field} />}
        />
        {errors.events && (
          <Typography
            data-cy="error-message-eventsDropdown"
            variant="caption"
            color="error"
          >
            <FormattedMessage id="containers.settings.extensions.createExtension.events.required" />
          </Typography>
        )}
      </Stack>
      {watchEvents.includes('invocation.scheduled') && (
        <Stack spacing={1}>
          <Stack alignItems="flex-start" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2">
                <FormattedMessage id="containers.settings.extensions.createExtension.schedule.label" />
              </Typography>
              <Tooltip
                title={
                  <FormattedMessage
                    id="containers.settings.extensions.createExtension.schedule.tooltip"
                    values={{
                      link: link(EXTERNAL_CRONTAB_VALIDATOR),
                      linebreak,
                    }}
                  />
                }
                placement="top"
              >
                <SvgIcon fontSize="medium" color="secondary">
                  <IconInfoSquareRounded />
                </SvgIcon>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="secondary">
              <FormattedMessage id="containers.settings.extensions.createExtension.schedule.subLabel" />
            </Typography>
          </Stack>
          <ValidationInput
            control={control}
            placeholder="*/10 * * * *"
            name="schedule"
            dataCy="extensions-webhook-schedule-input"
            getErrorMessage={(_, { message }) => message ?? ''}
          />
        </Stack>
      )}
      <Stack spacing={1}>
        <Stack alignItems="flex-start" spacing={1}>
          <Typography variant="body2">
            <FormattedMessage id="containers.settings.extensions.createExtension.queues.label" />
          </Typography>
          <Typography variant="body2" color="secondary">
            <FormattedMessage
              id="containers.settings.extensions.createExtension.queues.subLabel"
              values={{
                linebreak,
                italicText,
              }}
            />
          </Typography>
        </Stack>
        <Controller
          control={control}
          name="queues"
          render={({ field }) => (
            <QueueMultiSelect {...field} withLabel={false} />
          )}
        />
      </Stack>
      <Stack spacing={1}>
        <Typography variant="body2">
          <FormattedMessage id="containers.settings.extensions.createExtension.extensionType.label" />
        </Typography>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <ExtensionTypeSelect
            extensionType={extensionFunctionType}
            selected={watchType === extensionFunctionType}
            onClick={() => onTypeChange(extensionFunctionType)}
            dataCy="extension-type-function"
          />
          <ExtensionTypeSelect
            extensionType={extensionWebhookType}
            selected={watchType === extensionWebhookType}
            onClick={() => onTypeChange(extensionWebhookType)}
            dataCy="extension-type-webhook"
          />
        </Stack>
      </Stack>
      {watchType === extensionFunctionType && (
        <Stack spacing={1}>
          <Stack alignItems="flex-start" spacing={1}>
            <Typography variant="body2">
              <FormattedMessage id="containers.settings.extensions.createExtension.function.programmingLanguage.label" />
            </Typography>
            <Typography variant="body2" color="secondary">
              <FormattedMessage id="containers.settings.extensions.createExtension.function.programmingLanguage.subLabel" />
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <RuntimeButton
              selected={watchRuntime === defaultPythonRuntime}
              runtime={defaultPythonRuntime}
              onClick={() => onWatchRuntimeChange(defaultPythonRuntime)}
              dataCy={`extensions-function-runtime-${runtimesConfig[defaultPythonRuntime].type}`}
            />
            <RuntimeButton
              selected={watchRuntime === defaultNodejsRuntime}
              runtime={defaultNodejsRuntime}
              onClick={() => onWatchRuntimeChange(defaultNodejsRuntime)}
              dataCy={`extensions-function-runtime-${runtimesConfig[defaultNodejsRuntime].type}`}
            />
          </Stack>
        </Stack>
      )}
      {watchType === extensionWebhookType && (
        <>
          <Stack spacing={1} width={1}>
            <Stack alignItems="flex-start" spacing={1}>
              <Typography variant="body2">
                <FormattedMessage id="containers.settings.extensions.createExtension.webhook.url.label" />
              </Typography>
              <Typography variant="body2" color="secondary">
                <FormattedMessage id="containers.settings.extensions.createExtension.webhook.url.subLabel" />
              </Typography>
            </Stack>
            <ValidationInput<NewExtensionData>
              control={control}
              name="url"
              getErrorMessage={(_, { message }) => message ?? ''}
              dataCy="extensions-webhook-url-input"
              placeholder={intl.formatMessage({
                id: `containers.settings.extensions.createExtension.webhook.url.placeholder`,
              })}
            />
          </Stack>
          <Stack spacing={1} width={1}>
            <Stack
              alignItems="flex-start"
              direction="row"
              justifyContent="space-between"
            >
              <Stack alignItems="flex-start" spacing={1}>
                <Typography variant="body2">
                  <FormattedMessage id="containers.settings.extensions.createExtension.webhook.sharedSecret.label" />
                </Typography>
                <Typography
                  variant="body2"
                  color="secondary"
                  sx={{
                    a: {
                      color: t => t.palette.text.primary,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <FormattedMessage
                    id="containers.settings.extensions.createExtension.webhook.sharedSecret.subLabel"
                    values={{ link: link(docsLinks.validatingPayloads) }}
                  />
                </Typography>
              </Stack>
              <Typography variant="body2" color="secondary">
                <FormattedMessage id="containers.settings.extensions.createExtension.description.fieldDescription" />
              </Typography>
            </Stack>
            <ValidationInput
              autoComplete="new-password"
              control={control}
              name="secret"
              type={showSecret ? 'text' : 'password'}
              rightIconProps={{
                onClick: () => setShowSecret(!showSecret),
                'data-cy': 'eye-icon',
              }}
              rightIcon={
                <SvgIcon fontSize="large" color="secondary">
                  {showSecret ? <IconEyeOff /> : <IconEye />}
                </SvgIcon>
              }
              dataCy="extensions-webhook-secret-input"
              getErrorMessage={(_, { message }) => message ?? ''}
              placeholder={intl.formatMessage({
                id: 'containers.settings.extensions.createExtension.webhook.secret.placeholder',
              })}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};
